// HKDSE 2018 Paper 1 Knowledge Graph Samples
// Organized by question types with systematic naming

export const hkdse2018Samples = {
  algebra: [
    {
      id: 'hkdse2018_algebra_01',
      problem: "Given that $b$ is the subject of the formula $\\frac{a+4}{3} = \\frac{b+1}{2}$.",
      problemType: "algebra_formula",
      difficulty: "fundamental",
      description: "Rearranging formula to make a variable the subject",
      graphData: {
        nodes: [
          {
            id: "fraction_equation",
            name: "Fraction Equation",
            type: "concepts",
            description: "Equation with fractions on both sides",
            value: "(a+4)/3 = (b+1)/2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cross_multiplication",
            name: "Cross Multiplication",
            type: "concepts",
            description: "Method to eliminate fractions by cross multiplying",
            value: "If a/b = c/d, then ad = bc",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_cross_multiply",
            name: "Step 1: Cross Multiply",
            type: "steps",
            description: "Cross multiply to eliminate fractions",
            value: "2(a+4) = 3(b+1)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_expand",
            name: "Step 2: Expand",
            type: "steps",
            description: "Expand both sides",
            value: "2a + 8 = 3b + 3",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_collect_terms",
            name: "Step 3: Collect Terms",
            type: "steps",
            description: "Collect all terms with b on one side",
            value: "2a + 5 = 3b",
            importance: 8,
            x: 0, y: 100, z: -15
          },
          {
            id: "step4_solve_for_b",
            name: "Step 4: Solve for b",
            type: "steps",
            description: "Divide both sides by 3",
            value: "b = (2a + 5)/3",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "b expressed in terms of a",
            value: "b = (2a + 5)/3",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "fraction_equation", to: "cross_multiplication", type: "requires", strength: 9 },
          { from: "cross_multiplication", to: "step1_cross_multiply", type: "enables", strength: 10 },
          { from: "step1_cross_multiply", to: "step2_expand", type: "leads to", strength: 9 },
          { from: "step2_expand", to: "step3_collect_terms", type: "leads to", strength: 9 },
          { from: "step3_collect_terms", to: "step4_solve_for_b", type: "leads to", strength: 10 },
          { from: "step4_solve_for_b", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'hkdse2018_algebra_02',
      problem: "Simplify $\\frac{xy^7}{(x^{-2}y^3)^4}$ and express the answer in positive index form.",
      problemType: "algebra_indices",
      difficulty: "intermediate",
      description: "Simplifying expressions with negative indices",
      graphData: {
        nodes: [
          {
            id: "negative_indices",
            name: "Negative Indices",
            type: "concepts",
            description: "Understanding negative indices and their properties",
            value: "a⁻ⁿ = 1/aⁿ",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "power_of_power",
            name: "Power of Power",
            type: "concepts",
            description: "When raising a power to another power, multiply the indices",
            value: "(aᵐ)ⁿ = aᵐⁿ",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "division_of_powers",
            name: "Division of Powers",
            type: "concepts",
            description: "When dividing powers with the same base, subtract the indices",
            value: "aᵐ ÷ aⁿ = aᵐ⁻ⁿ",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_simplify_denominator",
            name: "Step 1: Simplify Denominator",
            type: "steps",
            description: "Apply power of power rule to (x⁻²y³)⁴",
            value: "(x⁻²y³)⁴ = x⁻⁸y¹²",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_divide",
            name: "Step 2: Divide",
            type: "steps",
            description: "Divide xy⁷ by x⁻⁸y¹²",
            value: "xy⁷ ÷ (x⁻⁸y¹²) = x⁹y⁻⁵",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "step3_convert_negative",
            name: "Step 3: Convert to Positive Indices",
            type: "steps",
            description: "Convert negative indices to positive using reciprocal",
            value: "x⁹y⁻⁵ = x⁹/y⁵",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "The simplified expression in positive indices",
            value: "x⁹/y⁵",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "negative_indices", to: "step3_convert_negative", type: "enables", strength: 10 },
          { from: "power_of_power", to: "step1_simplify_denominator", type: "enables", strength: 9 },
          { from: "division_of_powers", to: "step2_divide", type: "enables", strength: 9 },
          { from: "step1_simplify_denominator", to: "step2_divide", type: "leads to", strength: 10 },
          { from: "step2_divide", to: "step3_convert_negative", type: "leads to", strength: 10 },
          { from: "step3_convert_negative", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  probability: [
    {
      id: 'hkdse2018_probability_01',
      problem: "A box contains $n$ white balls, $5$ black balls and $8$ red balls. If a ball is randomly drawn from the box, the probability of drawing a red ball is $\\frac{2}{5}$. Find the value of $n$.",
      problemType: "probability_unknown",
      difficulty: "intermediate",
      description: "Finding unknown value using probability",
      graphData: {
        nodes: [
          {
            id: "probability_formula",
            name: "Probability Formula",
            type: "formulas",
            description: "P(event) = favorable outcomes / total outcomes",
            value: "P = favorable/total",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "total_balls",
            name: "Total Balls",
            type: "concepts",
            description: "Total number of balls in the box",
            value: "n + 5 + 8 = n + 13",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "red_balls",
            name: "Red Balls",
            type: "concepts",
            description: "Number of red balls",
            value: "8 red balls",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_setup_equation",
            name: "Step 1: Setup Equation",
            type: "steps",
            description: "Set up probability equation",
            value: "8/(n + 13) = 2/5",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_cross_multiply",
            name: "Step 2: Cross Multiply",
            type: "steps",
            description: "Cross multiply to solve for n",
            value: "40 = 2(n + 13)",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "step3_solve",
            name: "Step 3: Solve for n",
            type: "steps",
            description: "Solve the equation",
            value: "40 = 2n + 26, 2n = 14, n = 7",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "The value of n",
            value: "n = 7",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "probability_formula", to: "step1_setup_equation", type: "enables", strength: 10 },
          { from: "total_balls", to: "step1_setup_equation", type: "enables", strength: 9 },
          { from: "red_balls", to: "step1_setup_equation", type: "enables", strength: 9 },
          { from: "step1_setup_equation", to: "step2_cross_multiply", type: "leads to", strength: 10 },
          { from: "step2_cross_multiply", to: "step3_solve", type: "leads to", strength: 10 },
          { from: "step3_solve", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ]
}; 